import { useDocumentTitle } from "@/shared/hooks/useDocumentTitle";
import { useSettingsCache, useAuthCache } from "@/shared/cache";
import { useTheme, type Theme } from "@/shared/hooks/useTheme";
import { usePalette, type Palette } from "@/shared/hooks/usePalette";
import { useContrast, type Contrast } from "@/shared/hooks/useContrast";
import { useTextSize } from "@/shared/hooks/useTextSize";
import { Input } from "@/v2/components/ui/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/shared/components/ui/select";
import { Slider } from "@/shared/components/ui/slider";
import { Avatar, AvatarFallback } from "@/v2/components/ui/avatar";
import { SettingRow } from "@/v2/components/settings/SettingRow";
import { SectionHeading } from "@/v2/components/settings/SectionHeading";
import { cn } from "@/v2/lib/utils";
import { Sun, Moon, Monitor, ALargeSmall } from "lucide-react";

const themeOptions: { value: Theme; label: string; icon: typeof Sun }[] = [
	{ value: "light", label: "Light", icon: Sun },
	{ value: "dark", label: "Dark", icon: Moon },
	{ value: "system", label: "System", icon: Monitor },
];

function SegmentedThemeControl({
	value,
	onChange,
}: {
	value: Theme;
	onChange: (t: Theme) => void;
}) {
	return (
		<div className="inline-flex items-center rounded-lg border border-border bg-muted p-0.5">
			{themeOptions.map((opt) => (
				<button
					key={opt.value}
					onClick={() => onChange(opt.value)}
					className={cn(
						"inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
						value === opt.value
							? "bg-background text-foreground shadow-sm"
							: "text-muted-foreground hover:text-foreground",
					)}
				>
					<opt.icon className="size-4" />
					{opt.label}
				</button>
			))}
		</div>
	);
}

export default function Preferences() {
	useDocumentTitle("General");
	const { settings, updateSettings } = useSettingsCache();
	const { auth } = useAuthCache();
	const { theme, setTheme } = useTheme();
	const { palette, setPalette } = usePalette();
	const { contrast, setContrast } = useContrast();
	const { textSize, setTextSize } = useTextSize();

	return (
		<div>
			<h2 className="text-lg font-semibold mb-4">General</h2>

			<SectionHeading>Profile</SectionHeading>
			<div className="flex flex-col sm:flex-row gap-4 px-2 py-4 border-b border-border/40">
				<div className="sm:w-1/3 flex flex-col gap-1.5">
					<label className="text-sm font-medium">Name</label>
					<div className="flex items-center gap-2">
						<Avatar className="h-8 w-8 shrink-0">
							<AvatarFallback className="bg-primary text-primary-foreground text-xs font-medium">
								{auth.initials}
							</AvatarFallback>
						</Avatar>
						<Input
							value={auth.displayName}
							disabled
							className="w-full opacity-70 cursor-not-allowed"
						/>
					</div>
				</div>
				<div className="sm:w-2/3 flex flex-col gap-1.5">
					<label className="text-sm font-medium">Email</label>
					<Input
						value={auth.email}
						disabled
						className="w-full opacity-70 cursor-not-allowed"
					/>
				</div>
			</div>
			<SettingRow
				title="Practice area"
				description="Your area of legal practice, used to tailor generated narratives"
				stacked
			>
				<Input
					className="max-w-xs"
					value={settings.practice_type}
					onChange={(e) =>
						updateSettings({ practice_type: e.target.value })
					}
				/>
			</SettingRow>
			<SettingRow
				title="Role"
				description="Your professional role (e.g. Associate, Partner)"
				stacked
			>
				<Input
					className="max-w-xs"
					value={settings.prof_type}
					onChange={(e) =>
						updateSettings({ prof_type: e.target.value })
					}
				/>
			</SettingRow>

			<SectionHeading>Billing</SectionHeading>
			<SettingRow
				title="Minimum billable increments"
				description="Smallest time increment used when rounding billable hours"
			>
				<Select
					value={settings.minimum_units ? "0.2" : "0.1"}
					onValueChange={(v) => updateSettings({ minimum_units: v === "0.2" })}
				>
					<SelectTrigger className="w-36">
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="0.1">0.1 hours</SelectItem>
						<SelectItem value="0.2">0.2 hours</SelectItem>
					</SelectContent>
				</Select>
			</SettingRow>

			<SectionHeading>Appearance</SectionHeading>
			<SettingRow
				title="Color theme"
				description="Choose between light, dark, or system theme"
				stacked
			>
				<SegmentedThemeControl value={theme} onChange={setTheme} />
			</SettingRow>

			<div className="px-2 py-4 border-b border-border/40">
				<p className="text-sm font-medium mb-1">Style</p>
				<p className="text-sm text-muted-foreground mb-3">Choose a color palette</p>
				<div className="grid grid-cols-2 gap-4 max-w-[340px]">
					{([
						{
							value: "brand" as const,
							label: "Hourglass",
							accent: "var(--brand-900)",
							previewBg: "var(--surface-1)",
							previewSidebar: "var(--brand-900)",
							previewBorder: "var(--border)",
						},
						{
							value: "default" as const,
							label: "Standard",
							accent: "var(--neutral-600)",
							previewBg: "var(--surface-1)",
							previewSidebar: "var(--neutral-600)",
							previewBorder: "var(--border)",
						},
					]).map(({ value, label, accent, previewBg, previewSidebar, previewBorder }) => {
						const isActive = palette === value;
						return (
							<button
								key={value}
								type="button"
								onClick={() => setPalette(value as Palette)}
								className={cn(
									"group rounded-lg border-2 overflow-hidden transition-colors text-left",
									isActive
										? "border-primary ring-1 ring-primary/20"
										: "border-border hover:border-muted-foreground/40",
								)}
							>
								<div className="p-3" style={{ backgroundColor: previewBg }}>
									<div className="flex gap-1.5 h-[52px]">
										<div
											className="w-6 rounded flex flex-col gap-1 p-1"
											style={{ backgroundColor: previewSidebar }}
										>
											<div className="h-1 w-full rounded-full bg-white/30" />
											<div className="h-1 w-full rounded-full bg-white/30" />
										</div>
										<div className="flex-1 flex flex-col gap-1">
											<div
												className="h-2 w-1/3 rounded"
												style={{ backgroundColor: accent, opacity: 0.3 }}
											/>
											<div
												className="flex-1 rounded bg-white p-1.5 flex flex-col gap-1"
												style={{ border: `1px solid ${previewBorder}` }}
											>
												<div
													className="h-1 w-full rounded-full"
													style={{ backgroundColor: accent, opacity: 0.2 }}
												/>
												<div
													className="h-1 w-4/5 rounded-full"
													style={{ backgroundColor: accent, opacity: 0.15 }}
												/>
											</div>
										</div>
									</div>
								</div>
								<div
									className={cn(
										"flex items-center gap-2 px-3 py-2.5 border-t",
										isActive ? "border-primary/20 bg-primary/5" : "border-border",
									)}
								>
									<div className="h-3 w-3 rounded-full" style={{ backgroundColor: accent }} />
									<span
										className={cn(
											"text-sm font-medium",
											isActive ? "text-primary" : "text-muted-foreground",
										)}
									>
										{label}
									</span>
								</div>
							</button>
						);
					})}
				</div>
			</div>

			<div className="px-2 py-4 border-b border-border/40">
				<p className="text-sm font-medium mb-1">Contrast</p>
				<p className="text-sm text-muted-foreground mb-3">Adjust contrast level</p>
				<div className="grid grid-cols-2 gap-4 max-w-[340px]">
					{([
						{
							value: "regular" as const,
							label: "Regular",
							previewBg: "var(--surface-1)",
							previewFg: "var(--neutral-950)",
							previewBorder: "var(--border)",
						},
						{
							value: "high" as const,
							label: "High Contrast",
							previewBg: "#ffffff",
							previewFg: "#000000",
							previewBorder: "#000000",
						},
					]).map(({ value, label, previewBg, previewFg, previewBorder }) => {
						const isActive = contrast === value;
						return (
							<button
								key={value}
								type="button"
								onClick={() => setContrast(value as Contrast)}
								className={cn(
									"group rounded-lg border-2 overflow-hidden transition-colors text-left",
									isActive
										? "border-primary ring-1 ring-primary/20"
										: "border-border hover:border-muted-foreground/40",
								)}
							>
								<div className="p-3" style={{ backgroundColor: previewBg }}>
									<div className="flex flex-col gap-1.5 h-[40px] justify-center px-1">
										<div
											className="h-1.5 w-full rounded-full"
											style={{ backgroundColor: previewFg, opacity: 0.8 }}
										/>
										<div
											className="h-1.5 w-3/4 rounded-full"
											style={{ backgroundColor: previewFg, opacity: 0.5 }}
										/>
										<div className="h-px w-full" style={{ backgroundColor: previewBorder }} />
									</div>
								</div>
								<div
									className={cn(
										"flex items-center justify-center px-3 py-2.5 border-t",
										isActive ? "border-primary/20 bg-primary/5" : "border-border",
									)}
								>
									<span
										className={cn(
											"text-sm font-medium",
											isActive ? "text-primary" : "text-muted-foreground",
										)}
									>
										{label}
									</span>
								</div>
							</button>
						);
					})}
				</div>
			</div>

			<div className="px-2 py-4 border-b border-border/40">
				<p className="text-sm font-medium mb-1">Text size</p>
				<div className="flex items-center gap-3 mt-3">
					<ALargeSmall className="h-4 w-4 text-muted-foreground shrink-0" />
					<Slider
						value={[["small", "medium", "large"].indexOf(textSize)]}
						min={0}
						max={2}
						step={1}
						onValueChange={([v]) =>
							setTextSize((["small", "medium", "large"] as const)[v])
						}
						className="w-48"
					/>
					<ALargeSmall className="h-6 w-6 text-muted-foreground shrink-0" />
				</div>
				<div className="flex justify-between w-48 ml-7 mt-1.5">
					{(["Small", "Medium", "Large"] as const).map((label) => (
						<span key={label} className="text-[11px] text-muted-foreground">
							{label}
						</span>
					))}
				</div>
			</div>
		</div>
	);
}
